import { useEffect, useState } from 'react'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'

import MotionImage from '@/components/common/Motion/MotionImage'
import MotionText from '@/components/common/Motion/MotionText'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import LanguageSelector from '../LanguageSelector/LanguageSelector'

export interface Slide {
  image: string
  title: string
  description: string
}

interface SlideshowProps {
  slides: Slide[]
  duration?: number
  className?: string
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 200 : -200,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 200 : -200,
    opacity: 0
  })
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

export const Slides = ({
  slides,
  duration = 5000,
  className = ""
}: SlideshowProps) => {
  const [[currentSlide, direction], setSlide] = useState([0, 0]);
  const [step, setStep] = useState(0);

  const slideIndex = ((currentSlide % slides.length) + slides.length) % slides.length;
  const updateInterval = 500;
  const totalSteps = duration / updateInterval;
  const progress = step / totalSteps;

  const paginate = (newDirection: number) => {
    setStep(0);
    setSlide(prev => [prev[0] + newDirection, newDirection]);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setStep(prev => {
        if (prev >= totalSteps) {
          paginate(1);
          return 0;
        }
        return prev + 1;
      });
    }, updateInterval);

    return () => clearInterval(timer);
  }, [totalSteps]);

  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
    const swipe = swipePower(offset.x, velocity.x);

    if (swipe < -swipeConfidenceThreshold) {
      paginate(1);
    } else if (swipe > swipeConfidenceThreshold) {
      paginate(-1);
    }
  };

  const handleTap = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const halfWidth = rect.width / 2;

    if (x < halfWidth) {
      paginate(-1);
    } else {
      paginate(1);
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <motion.div className="flex flex-row" exit={{ opacity: 0 }}>
        <ProgressBar
          currentStep={slideIndex + progress}
          totalSteps={slides.length}
        />
        <LanguageSelector />
      </motion.div>
      <motion.div
        className="flex-1 relative overflow-hidden"
        exit={{ opacity: 0 }}>
        <AnimatePresence mode="sync" custom={direction}>
          <motion.div
            key={slideIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={handleDragEnd}
            onClick={handleTap}
            className="absolute inset-0 flex flex-col touch-pan-y"
          >
            <MotionImage
              className="h-[14.5rem] w-[14.5rem] mt-3 mx-auto object-cover"
              src={slides[slideIndex].image}
              alt={slides[slideIndex].title}
            />
            <div className="space-y-4">
              <MotionText
                as="h2"
                className="text-[2.5rem] font-hubot font-black uppercase leading-none text-center"
                delay={0.2}
              >
                {slides[slideIndex].title}
              </MotionText>
              <MotionText
                as="p"
                className="text-zinc-700 text-base text-center"
                delay={0.4}
              >
                {slides[slideIndex].description}
              </MotionText>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Slides;